import {NgModule} from '@angular/core';
import {Routes, RouterModule} from '@angular/router';

import {LoginComponent} from './pages/login.component';
import {ManualComponent} from './manual/manual.component';
import {AuthGuard} from './services/auth.guard';
import {CapabilityGuard} from './services/capability.guard';
import {CAPS} from './services/roles';
import {AdminContainerComponent} from './containers/admin-container.component';
import {UserContainerComponent} from './containers/user-container.component';
import {KaitoriyaContainerComponent, KaitoriyaPlaceholderComponent, KaitoriyaCustomerManagementComponent, KaitoriyaProductManagementComponent, KaitoriyaMasterManagementComponent, KaitoriyaOrderManagementComponent, KaitoriyaBbManagementComponent, KaitoriyaUserManagementComponent, KaitoriyaAnalysisManagementComponent, KaitoriyaCalendarManagementComponent} from './kaitoriya';
import {SystemContainerComponent, SystemMailComponent, SystemUsersComponent} from './system';
import {AuctionManagementComponent, HistoryManagementComponent, ItemManagementComponent, ItemsManagementComponent, MasterManagementComponent, NewsManagementComponent, PreferenceManagementComponent, ReportManagementComponent, SummaryManagementComponent, UserManagementComponent} from './admin';
import {AuctionComponent, BidComponent, ItemComponent, ItemsComponent, LiveComponent, SummaryComponent, UserComponent} from './user';
import {KobeacContainerComponent, KobeacHistoryComponent, KobeacInputComponent, KobeacSummaryComponent, KobeacMasterComponent, KobeacAnalyzeComponent, KobeacDesignerComponent, KobeacLedgerSearchComponent, KobeacDashboardComponent, KobeacProfileComponent, KobeacDocumentComponent, KobeacMemberComponent, KobeacLedgerListComponent, KobeacInvoiceEditComponent, KobeacLedgerHomeComponent, KobeacNotificationComponent} from './kobeac';
import {ChangePasswordComponent} from './pages/change-password.component';
import {ResetPasswordComponent} from './pages/reset-password.component';

// data.title は翻訳キー (route.*)。表示側 (breadcrumbs / manual / kaitoriya-placeholder 等) で transloco を通す。
export const routes: Routes = [
    {
        path: '',
        redirectTo: 'login',
        pathMatch: 'full',
    },
    {
        path: 'login',
        component: LoginComponent,
        data: {
            title: 'route.login'
        }
    },
    {
        path: 'change-password',
        component: ChangePasswordComponent,
        data: {
            title: 'route.password'
        }
    },
    {
        path: 'reset-password',
        component: ResetPasswordComponent,
        data: {
            title: 'route.reset'
        }
    },
    // マニュアル: ユーザ用は公開 (ログイン不要)。神戸オークション/買取屋/オンラインオークションで
    // 別々のマニュアルに分冊。管理者用・開発者用はログイン必須 (AuthGuard)。
    {
        path: 'manual/user/kobe',
        component: ManualComponent,
        data: {title: 'route.manual_user_kobe', file: 'user-kobe.md', pub: true},
    },
    {
        path: 'manual/user/kaitoriya',
        component: ManualComponent,
        data: {title: 'route.manual_user_kaitoriya', file: 'user-kaitoriya.md', pub: true},
    },
    {
        path: 'manual/user/online',
        component: ManualComponent,
        data: {title: 'route.manual_user_online', file: 'user-online.md', pub: true},
    },
    // 旧 URL 後方互換: 神戸オークション版へ。
    {path: 'manual/user', redirectTo: 'manual/user/kobe', pathMatch: 'full'},
    {
        path: 'manual/admin',
        component: ManualComponent,
        canActivate: [AuthGuard, CapabilityGuard],
        data: {title: 'route.manual_admin', file: 'admin-manual.md', capability: [CAPS.KOBE_ADMIN, CAPS.KAITORIYA_ADMIN, CAPS.ONLINE_ADMIN]},
    },
    {
        path: 'manual/developer',
        component: ManualComponent,
        canActivate: [AuthGuard, CapabilityGuard],
        data: {title: 'route.manual_developer', file: 'developer-manual.md', capability: CAPS.SYSTEM},
    },
    {
        path: 'user',
        component: UserContainerComponent,
        data: {
            title: 'route.menu'
        },
        canActivate: [AuthGuard],
        children: [
            {
                path: '',
                redirectTo: 'user/live',
                pathMatch: 'full'
            },
            {
                path: 'live',
                component: LiveComponent,
                data: {
                    title: 'route.live_auction'
                }
            },
            {
                path: 'bid',
                component: BidComponent,
                data: {
                    title: 'route.pre_bid'
                }
            },
            {
                path: 'item/:id',
                component: ItemComponent,
                data: {
                    title: 'route.item_info'
                }
            },
            {
                path: 'items',
                component: ItemsComponent,
                data: {
                    title: 'route.item_list'
                }
            },
            {
                path: 'auction',
                component: AuctionComponent,
                data: {
                    title: 'route.auction_info'
                }
            },
            {
                path: 'summary',
                component: SummaryComponent,
                data: {
                    title: 'route.settlement_info'
                }
            },
            {
                path: 'user',
                component: UserComponent,
                data: {
                    title: 'route.member_info'
                }
            },
        ]
    },
    {
        path: 'admin',
        component: AdminContainerComponent,
        data: {
            title: 'route.menu',
            capability: CAPS.ONLINE_ADMIN
        },
        canActivate: [AuthGuard, CapabilityGuard],
        children: [
            {
                path: '',
                redirectTo: 'admin/item-management',
                pathMatch: 'full'
            },
            {
                path: 'auction-management',
                component: AuctionManagementComponent,
                data: {
                    title: 'route.auction_info_management'
                }
            },
            {
                path: 'news-management',
                component: NewsManagementComponent,
                data: {
                    title: 'route.news_management'
                }
            },
            {
                path: 'history-management',
                component: HistoryManagementComponent,
                data: {
                    title: 'route.bid_history_management'
                }
            },
            {
                path: 'item-management',
                component: ItemManagementComponent,
                data: {
                    title: 'route.listing_management'
                }
            },
            {
                path: 'items-management',
                component: ItemsManagementComponent,
                data: {
                    title: 'route.bulk_listing_management'
                }
            },
            {
                path: 'master-management',
                component: MasterManagementComponent,
                data: {
                    title: 'route.master_management'
                }
            },
            {
                path: 'preference-management',
                component: PreferenceManagementComponent,
                data: {
                    title: 'route.initial_settings'
                }
            },
            {
                path: 'report-management',
                component: ReportManagementComponent,
                data: {
                    title: 'route.report_management'
                }
            },
            {
                path: 'summary-management',
                component: SummaryManagementComponent,
                data: {
                    title: 'route.settlement_management'
                }
            },
            {
                path: 'user-management',
                component: UserManagementComponent,
                data: {
                    title: 'route.member_info_management'
                }
            }
        ]
    },
    {
        path: 'kobe',
        component: KobeacContainerComponent,
        data: {
            title: 'route.active_carry'
        },
        canActivate: [AuthGuard],
        children: [
            {path: '', redirectTo: 'dashboard', pathMatch: 'full'},
            {path: 'dashboard', component: KobeacDashboardComponent, data: {title: 'route.dashboard'}},
            {path: 'auction/input', component: KobeacInputComponent, canActivate: [CapabilityGuard], data: {title: 'route.item_management', capability: CAPS.KOBE_ADMIN}},
            {path: 'auction/history', component: KobeacHistoryComponent, canActivate: [CapabilityGuard], data: {title: 'route.trade_history', capability: CAPS.KOBE_ADMIN}},
            {path: 'auction/summary', component: KobeacSummaryComponent, canActivate: [CapabilityGuard], data: {title: 'route.settlement_statement', capability: CAPS.KOBE_ADMIN}},
            {path: 'report/analyze', component: KobeacAnalyzeComponent, canActivate: [CapabilityGuard], data: {title: 'route.analysis', capability: CAPS.KOBE_ADMIN}},
            {path: 'documents', component: KobeacDocumentComponent, data: {title: 'route.documents'}},
            {path: 'admin/master', component: KobeacMasterComponent, canActivate: [CapabilityGuard], data: {title: 'route.master_management', capability: CAPS.KOBE_ADMIN}},
            {path: 'admin/auction', component: KobeacMasterComponent, canActivate: [CapabilityGuard], data: {title: 'route.auction_management', capability: CAPS.KOBE_ADMIN, master: 'オークション'}},
            {path: 'admin/customer', component: KobeacMasterComponent, canActivate: [CapabilityGuard], data: {title: 'route.customer_management', capability: CAPS.KOBE_ADMIN, master: '顧客'}},
            {path: 'admin/member', component: KobeacMemberComponent, canActivate: [CapabilityGuard], data: {title: 'route.member_management', capability: CAPS.KOBE_ADMIN}},
            {path: 'admin/notification', component: KobeacNotificationComponent, canActivate: [CapabilityGuard], data: {title: 'route.notification_management', capability: CAPS.KOBE_ADMIN}},
            {path: 'admin/ledger', component: KobeacLedgerHomeComponent, canActivate: [CapabilityGuard], data: {title: 'route.trade_ledger', capability: CAPS.KOBE_ADMIN}},
            {path: 'admin/ledgers', component: KobeacLedgerListComponent, canActivate: [CapabilityGuard], data: {title: 'route.trade_ledger_list', capability: CAPS.KOBE_ADMIN}},
            {path: 'admin/invoice', component: KobeacInvoiceEditComponent, canActivate: [CapabilityGuard], data: {title: 'route.invoice_edit', capability: CAPS.KOBE_ADMIN}},
            {path: 'admin/ledger-search', component: KobeacLedgerSearchComponent, canActivate: [CapabilityGuard], data: {title: 'route.ledger_search', capability: CAPS.KOBE_ADMIN}},
            {path: 'admin/file', redirectTo: 'admin/ledger-search', pathMatch: 'full'},
            {path: 'profile', component: KobeacProfileComponent, data: {title: 'route.profile'}},
        ]
    },
    {
        path: 'kaitoriya',
        component: KaitoriyaContainerComponent,
        data: {
            title: 'route.kaitoriya',
            capability: CAPS.KAITORIYA_VIEW
        },
        canActivate: [AuthGuard, CapabilityGuard],
        children: [
            {path: '', redirectTo: 'orders', pathMatch: 'full'},
            // 一般メニュー (KAITORIYA_VIEW)
            {path: 'orders', component: KaitoriyaOrderManagementComponent, data: {title: 'route.purchase_slips'}},
            {path: 'bb', component: KaitoriyaBbManagementComponent, data: {title: 'route.purchase_trade'}},
            {path: 'analysis', component: KaitoriyaAnalysisManagementComponent, data: {title: 'route.purchase_analysis'}},
            {path: 'calendar', component: KaitoriyaCalendarManagementComponent, data: {title: 'route.calendar'}},
            {path: 'products', component: KaitoriyaProductManagementComponent, data: {title: 'route.product_management'}},
            // 管理者メニュー (KAITORIYA_ADMIN)
            {path: 'customers', component: KaitoriyaCustomerManagementComponent, canActivate: [CapabilityGuard], data: {title: 'route.customer_management', capability: CAPS.KAITORIYA_ADMIN}},
            {path: 'master', component: KaitoriyaMasterManagementComponent, canActivate: [CapabilityGuard], data: {title: 'route.master_management', capability: CAPS.KAITORIYA_ADMIN}},
            {path: 'users', component: KaitoriyaUserManagementComponent, canActivate: [CapabilityGuard], data: {title: 'route.user_management', capability: CAPS.KAITORIYA_ADMIN}},
            {path: 'reports', component: KaitoriyaPlaceholderComponent, canActivate: [CapabilityGuard], data: {title: 'route.reports', capability: CAPS.KAITORIYA_ADMIN}},
        ]
    },
    {
        path: 'system',
        component: SystemContainerComponent,
        data: {
            title: 'route.system_management',
            capability: CAPS.SYSTEM
        },
        canActivate: [AuthGuard, CapabilityGuard],
        children: [
            {path: '', redirectTo: 'users', pathMatch: 'full'},
            {path: 'users', component: SystemUsersComponent, canActivate: [CapabilityGuard], data: {title: 'route.user_management', capability: CAPS.SYSTEM}},
            // 各ラインのレポート/デザイナー/レポート管理は同一機能のためシステム管理に統合。
            {path: 'reports', component: KobeacDesignerComponent, canActivate: [CapabilityGuard], data: {title: 'route.report_management', capability: CAPS.SYSTEM}},
            // メール送信 (SMTP) 設定とテンプレート。backend が 4 アプリ共有なのでここに 1 つ置く (issue #171)
            {path: 'mail', component: SystemMailComponent, canActivate: [CapabilityGuard], data: {title: 'route.mail_settings', capability: CAPS.SYSTEM}},
        ]
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
